import i18n, { LOCALIZED_PACK_UNITS } from '@/utils/i18n';
import React from 'react';
import { createStyles } from '@/contexts/theme-context';
import { formatDateWithYear } from '@/utils/date';
import { ItemListOption } from '@/contexts/item-context-types';
import { ListDisplaySize } from '@/contexts/order-guide-settings-context';
import { OrderableItemListItemImage } from '@/components/orders/orderable-item-list-item-image';
import { SCREEN_PADDING_SMALL } from '@/styles';
import { Text, View } from 'react-native';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useItemDisplayOptions } from '@/contexts/new-order-context';

export interface Props {
  guaranteed?: boolean;
  isSimplified?: boolean;
  mostRecentInvoiceDate?: string | null;
  option: ItemListOption;
}

export const CreditRequestVariantPackListItem: React.FC<Props> = ({
  guaranteed,
  isSimplified = false,
  mostRecentInvoiceDate,
  option,
}) => {
  const { hidePlaceholderImages } = useItemDisplayOptions();
  const [styles, theme] = useStyles();
  const metadata = option.metadata!;
  const packUnit = metadata.packUnit;
  const displayUnit = LOCALIZED_PACK_UNITS[packUnit] || packUnit;
  const hasGuaranteeInformation = guaranteed !== undefined;
  const formattedDate = mostRecentInvoiceDate
    ? formatDateWithYear(mostRecentInvoiceDate)
    : null;

  return (
    <View style={styles.container}>
      <View collapsable={false}>
        <OrderableItemListItemImage
          alt={[metadata.displayName, metadata.externalID, displayUnit]
            .filter(Boolean)
            .join(' • ')}
          hidePlaceholderImages={hidePlaceholderImages}
          imageURLs={metadata.photoURLs ?? []}
          listDisplaySize={
            isSimplified ? ListDisplaySize.Medium : ListDisplaySize.Standard // todo: check this
          }
          style={styles.imagePreview}
        />
      </View>
      <View style={styles.details}>
        <Typography numberOfLines={1} variant="heading-sm">
          {metadata.displayName}
        </Typography>
        <Text numberOfLines={1}>
          <Typography
            color={theme.sk.color.text.tertiary}
            variant="paragraph-md"
          >
            {metadata.externalID ? `${metadata.externalID} • ` : ''}
          </Typography>
          <Typography
            color={theme.sk.color.text.tertiary}
            textTransform="capitalize"
            variant="paragraph-md"
          >
            {displayUnit}
          </Typography>
        </Text>

        <View style={styles.creditItemInfo}>
          {hasGuaranteeInformation ? (
            <Typography
              color={
                guaranteed
                  ? theme.sk.color.text.success
                  : theme.sk.color.text.warning
              }
              variant="paragraph-sm-600"
            >
              {i18n.t(
                guaranteed
                  ? 'credit_request.guaranteed'
                  : 'credit_request.not_guaranteed',
              )}
            </Typography>
          ) : null}
          {isSimplified ? null : (
            <Typography
              color={theme.sk.color.text.tertiary}
              variant="paragraph-sm-600"
            >
              {hasGuaranteeInformation ? ' • ' : ''}
              {mostRecentInvoiceDate
                ? i18n.t('fulfilled_date', { date: formattedDate })
                : i18n.t('never_fulfilled')}
            </Typography>
          )}
        </View>
      </View>
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  container: {
    alignItems: 'flex-start',
    flex: 1,
    flexDirection: 'row',
    padding: SCREEN_PADDING_SMALL,
  },
  creditItemInfo: {
    flexDirection: 'row',
    marginTop: 4,
  },
  details: {
    alignSelf: 'center',
    flex: 1,
    marginLeft: 16,
  },
  imagePreview: {
    borderColor: theme.sk.color.border.image,
    borderRadius: 8,
    borderWidth: 1,
  },
}));
